// src/page/UserDetail.js
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

function UserDetail() {
    useAuthGuard();
    const { userID } = useParams();

    // ---- helpers ----
    const pad2 = (n) => String(n).padStart(2, '0');
    const fmtDateTime = (s) => {
        if (!s) return '';
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return '';
        return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    };
    const sexLabel = (v) => {
        const m = { '0': '不明', '1': '男性', '2': '女性' };
        const k = String(v ?? '');
        return m[k] ?? k;
    };

    // ---- state (display only) ----
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [user, setUser] = useState({});
    const [point, setPoint] = useState(null);
    const [extras, setExtras] = useState(null);

    // derived fields
    const userNumber = user.userNumber || '';
    const b2cEmail = user.b2cEmail || '';
    const lastName = user.lastName || '';
    const firstName = user.firstName || '';
    const lastNameKana = user.lastNameKana || '';
    const firstNameKana = user.firstNameKana || '';
    const sexType = sexLabel(user.sexType);
    const birthday = user.birthday ? fmtDateTime(user.birthday).slice(0, 10) : ''; // YYYY/MM/DD
    const zipcode = user.userZipcode || '';
    const addr1 = user.userAddress1 || '';
    const addr2 = user.userAddress2 || '';
    const addr3 = user.userAddress3 || '';
    const addr4 = user.userAddress4 || '';
    const notifFlag = user.notificationEnabledFlag ? 'ON' : 'OFF';
    const userLevel = (user.userLevel ?? '') + '';
    const createdAt = fmtDateTime(user.createdAt);
    const deletedAt = fmtDateTime(user.deletedAt);
    const lastLogin = fmtDateTime(user.lastLoginDate);
    const loginCount = (user.loginCount ?? '') + '';

    const pointTotal = point?.total ?? '';
    const pointUsable = point?.usable ?? '';

    // ---- 購入履歴（一覧＆ページングのみ） ----
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);

    // ---- 利用実績（一覧＆ページングのみ） ----
    const [usages, setUsages] = useState([]);
    const [usagesLoading, setUsagesLoading] = useState(false);
    const [usagesError, setUsagesError] = useState('');
    const [uPage, setUPage] = useState(1);
    const [uPageSize, setUPageSize] = useState(20);
    const [uTotal, setUTotal] = useState(0);
    const uPageCount = Math.max(1, Math.ceil((uTotal || 0) / uPageSize));
    const uFirst = uTotal === 0 ? 0 : (uPage - 1) * uPageSize + 1;
    const uLast = Math.min(uTotal, uPage * uPageSize);
    const uPages = useMemo(() => Array.from({ length: uPageCount }, (_, i) => i + 1), [uPageCount]);
    const uPrev = () => setUPage(p => Math.max(1, p - 1));
    const uNext = () => setUPage(p => Math.min(uPageCount, p + 1));

    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('access_token') || '';
                const res = await fetch(
                    `${API_BASE_URL}/v1/user/${encodeURIComponent(userID)}?includeDeleted=true`,
                    {
                        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        signal: ac.signal
                    }
                );
                if (!res.ok) {
                    let msg = `HTTP ${res.status}`;
                    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
                    throw new Error(msg);
                }
                const j = await res.json();
                if (j?.user) setUser(j.user);
                if (j?.point) setPoint(j.point);
                if (j?.extras) setExtras(j.extras);
            } catch (e) {
                if (e.name !== 'AbortError') setError('会員情報の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [userID]);

    // フォーマッタ（購入履歴用）
    const fmtInt = (v) => (v == null ? '' : String(v));
    const fmtJPY = (v) => (typeof v === 'number' ? v.toLocaleString('ja-JP') : (v ?? ''));
    const statusLabel = (s) => {
        const m = { "0": "購入開始", "1": "購入済み", "2": "利用中", "3": "利用済み", "4": "期限切れ", "5": "キャンセル(利用済み)", "6": "キャンセル(未使用)", "9": "取消" };
        return m[String(s ?? '')] ?? '';
    };

    // 購入履歴の取得（ソート不要、ページングのみ）
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setOrdersLoading(true);
                setOrdersError('');
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/summary/sales`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                url.searchParams.set('userid', userID); // サーバ側に追加済みのフィルタ
                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json();
                setOrders(Array.isArray(j.items) ? j.items : []);
                setTotal(Number(j.total) || 0);
                if (j.size && Number(j.size) !== pageSize) setPageSize(Number(j.size));
            } catch (e) {
                if (e.name !== 'AbortError') setOrdersError('購入履歴の取得に失敗しました');
            } finally {
                setOrdersLoading(false);
            }
        })();
        return () => ac.abort();
    }, [userID, page, pageSize]);

    // 利用実績の取得（ソート不要、ページングのみ）
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setUsagesLoading(true);
                setUsagesError('');
                const token = localStorage.getItem('access_token') || '';
                const url = new URL(`${API_BASE_URL}/v1/summary/usage`);
                url.searchParams.set('page', String(uPage));
                url.searchParams.set('size', String(uPageSize));
                url.searchParams.set('userid', userID); // バックエンドに追加済み
                const res = await fetch(url.toString(), {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const j = await res.json();
                setUsages(Array.isArray(j.items) ? j.items : []);
                setUTotal(Number(j.total) || 0);
                if (j.size && Number(j.size) !== uPageSize) setUPageSize(Number(j.size));
            } catch (e) {
                if (e.name !== 'AbortError') setUsagesError('利用実績の取得に失敗しました');
            } finally {
                setUsagesLoading(false);
            }
        })();
        return () => ac.abort();
    }, [userID, uPage, uPageSize]);

    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);
    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));

    const icCardNumber = extras?.icCardNumber ?? '';
    const shareCycleNo = extras?.shareCycleMemberNumber ?? '';
    const bpPointNo = extras?.bpPointMemberNumber ?? '';
    const becozNo = extras?.becozNumber ?? '';
    const ticketCount = extras?.ticketCount ?? '';

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/user">会員管理</a></li>
                    <li className="breadcrumb-item active">会員詳細</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">会員詳細情報</h2>
                    </div>
                </div>

                {loading && <div className="text-body-secondary mb-3">読み込み中…</div>}
                {!loading && error && <div className="text-danger mb-3">{error}</div>}

                {/* Tabs */}
                <ul
                    className="faq-category-tab nav nav-tabs nav-fill flex-nowrap w-100 mb-10 mb-md-5 pb-3 pt-2 mx-auto"
                    role="tablist"
                >
                    <li className="nav-item" role="presentation">
                        <button
                            className="nav-link fw-semibold fs-8 active text-nowrap"
                            id="tab-info"
                            type="button"
                            data-bs-toggle="tab"
                            data-bs-target="#pane-info"
                            aria-controls="pane-info"
                            aria-selected="true"
                            role="tab"
                        >
                            会員情報
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button
                            className="nav-link fw-semibold fs-8 text-nowrap"
                            id="tab-orders"
                            type="button"
                            data-bs-toggle="tab"
                            data-bs-target="#pane-orders"
                            aria-controls="pane-orders"
                            aria-selected="false"
                            role="tab"
                            tabIndex="-1"
                        >
                            購入履歴
                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button
                            className="nav-link fw-semibold fs-8 text-nowrap"
                            id="tab-usage"
                            type="button"
                            data-bs-toggle="tab"
                            data-bs-target="#pane-usage"
                            aria-controls="pane-usage"
                            aria-selected="false"
                            role="tab"
                            tabIndex="-1"
                        >
                            利用実績
                        </button>
                    </li>
                </ul>

                {/* Tab panes */}
                <div className="tab-content">
                    <div id="pane-info" className="tab-pane fade show active" role="tabpanel" aria-labelledby="tab-info" tabIndex="0">
                        <div className="row g-5">
                            {/* left */}
                            <div className="col-12 col-xl-8">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">会員ID</h4>
                                            <input className="form-control" type="text" value={userID} disabled />
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">会員番号</h4>
                                            <input className="form-control" type="text" value={userNumber} readOnly />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">メールアドレス</h4>
                                            <input className="form-control" type="text" value={b2cEmail} readOnly />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">姓名</h4>
                                            <div className="row g-2">
                                                <div className="col-6">
                                                    <input className="form-control" type="text" value={lastName} readOnly />
                                                </div>
                                                <div className="col-6">
                                                    <input className="form-control" type="text" value={firstName} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <div className="row g-2">
                                                <div className="col-6">
                                                    <h4 className="mb-3">性別</h4>
                                                    <input className="form-control" type="text" value={sexType} readOnly />
                                                </div>
                                                <div className="col-6">
                                                    <h4 className="mb-3">生年月日</h4>
                                                    <input className="form-control" type="text" value={birthday} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">姓名（カナ）</h4>
                                            <div className="row g-2">
                                                <div className="col-6">
                                                    <input className="form-control" type="text" value={lastNameKana} readOnly />
                                                </div>
                                                <div className="col-6">
                                                    <input className="form-control" type="text" value={firstNameKana} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <div className="row g-2">
                                                <div className="col-6">
                                                    <h4 className="mb-3">プッシュ通知受信フラグ</h4>
                                                    <input className="form-control" type="text" value={notifFlag} readOnly />
                                                </div>
                                                <div className="col-6">
                                                    <h4 className="mb-3">ユーザーレベル</h4>
                                                    <input className="form-control" type="text" value={userLevel} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">郵便番号</h4>
                                            <div className="row g-2">
                                                <div className="col-6">
                                                    <input className="form-control" type="text" value={zipcode} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <h4 className="mb-3">住所</h4>
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <input className="form-control" type="text" value={addr1} readOnly />
                                            <input className="form-control mt-2" type="text" value={addr2} readOnly />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <input className="form-control" type="text" value={addr3} readOnly />
                                            <input className="form-control mt-2" type="text" value={addr4} readOnly />
                                        </div>
                                    </div>
                                </div>

                                {/* 連携番号など（未取得は空のまま） */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">交通系ICカード番号</h4>
                                            <input className="form-control" type="text" value={icCardNumber} readOnly />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">シェアサイクル会員番号</h4>
                                            <input className="form-control" type="text" value={shareCycleNo} readOnly />
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">BPポイント会員番号</h4>
                                            <input className="form-control" type="text" value={bpPointNo} readOnly />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">Becoz会員番号</h4>
                                            <input className="form-control" type="text" value={becozNo} readOnly />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* right */}
                            <div className="col-12 col-xl-4">
                                <div className="row g-2">
                                    <div className="col-12 col-xl-12">
                                        <div className="card mb-3">
                                            <div className="card-body">
                                                <div className="row gx-3">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <div className="mb-4">
                                                            <h5 className="mb-3">登録日時</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <input className="form-control" type="text" value={createdAt} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row gx-3">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <div className="mb-4">
                                                            <h5 className="mb-3">退会日時</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <input className="form-control" type="text" value={deletedAt} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row gx-3">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <div className="mb-4">
                                                            <h5 className="mb-3">ログイン回数</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <input className="form-control" type="text" value={loginCount} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row gx-3">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <div className="mb-4">
                                                            <h5 className="mb-3">乗車券利用回数</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <input className="form-control" type="text" value={ticketCount} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row gx-3">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <div className="mb-4">
                                                            <h5 className="mb-3">前回ログイン日時</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <input className="form-control" type="text" value={lastLogin} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row gx-3">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <div className="mb-4">
                                                            <h5 className="mb-3">ポイント</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <input className="form-control" type="text" value={pointTotal} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row gx-3">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <div className="mb-4">
                                                            <h5 className="mb-3">ポイント（利用可能）</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <input className="form-control" type="text" value={pointUsable} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>{/* card-body */}
                                        </div>{/* card */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="pane-orders" className="tab-pane fade" role="tabpanel" aria-labelledby="tab-orders" tabIndex="0">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3">スポット名</th>
                                        <th className="align-middle ps-3">乗車券名</th>
                                        <th className="align-middle ps-3">大人</th>
                                        <th className="align-middle ps-3">小人</th>
                                        <th className="align-middle ps-3">購入日時</th>
                                        <th className="align-middle ps-3">決済金額</th>
                                        <th className="align-middle ps-3">取引ID</th>
                                        <th className="align-middle ps-3">エラーコード</th>
                                        <th className="align-middle ps-3">購入ステータス</th>
                                        <th className="align-middle ps-3">払い戻し手数料</th>
                                        <th className="align-middle ps-3">取り消し手数料</th>
                                        <th className="align-middle ps-3">払い戻し時刻</th>
                                        <th className="align-middle ps-3">キャンセルユーザー</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersError && (
                                        <tr>
                                            <td className="ps-3 text-danger" colSpan={13}>{ordersError}</td>
                                        </tr>
                                    )}
                                    {!ordersError && ordersLoading && (
                                        <tr>
                                            <td className="ps-3 text-body-tertiary" colSpan={13}>読み込み中…</td>
                                        </tr>
                                    )}
                                    {!ordersError && !ordersLoading && orders.length === 0 && (
                                        <tr>
                                            <td className="ps-3 text-body-tertiary" colSpan={13}>データがありません</td>
                                        </tr>
                                    )}
                                    {!ordersError && !ordersLoading && orders.map((r, i) => (
                                        <tr key={i}>
                                            <td className="ps-3">{r.spotName || ''}</td>
                                            <td className="ps-3">{r.packageName || ''}</td>
                                            <td className="ps-3">{fmtInt(r.countAdult)}</td>
                                            <td className="ps-3">{fmtInt(r.countChild)}</td>
                                            <td className="ps-3">{fmtDateTime(r.requestDate)}</td>
                                            <td className="ps-3">{fmtJPY(r.amount)}</td>
                                            <td className="ps-3">{r.tradeId || ''}</td>
                                            <td className="ps-3">{r.errorCode || ''}</td>
                                            <td className="ps-3">{r.paymentStatus || ''}</td>
                                            <td className="ps-3">{fmtJPY(r.refundFee)}</td>
                                            <td className="ps-3">{fmtJPY(r.cancellationFee)}</td>
                                            <td className="ps-3">{fmtDateTime(r.canceledAt)}</td>
                                            <td className="ps-3">{r.canceledBy || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                                </p>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>
                                <ul className="mb-0 pagination">
                                    {pages.map(p => (
                                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setPage(p)}>
                                                {p}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div id="pane-usage" className="tab-pane fade" role="tabpanel" aria-labelledby="tab-usage" tabIndex="0">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3">スポット名</th>
                                        <th className="align-middle ps-3">乗車券名</th>
                                        <th className="align-middle ps-3">取得日時</th>
                                        <th className="align-middle ps-3">利用日時</th>
                                        <th className="align-middle ps-3">有効期限</th>
                                        <th className="align-middle ps-3">ステータス</th>
                                        <th className="align-middle ps-3">大人</th>
                                        <th className="align-middle ps-3">小人</th>
                                        <th className="align-middle ps-3">バウチャー利用ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usagesError && (
                                        <tr>
                                            <td className="ps-3 text-danger" colSpan={9}>{usagesError}</td>
                                        </tr>
                                    )}
                                    {!usagesError && usagesLoading && (
                                        <tr>
                                            <td className="ps-3 text-body-tertiary" colSpan={9}>読み込み中…</td>
                                        </tr>
                                    )}
                                    {!usagesError && !usagesLoading && usages.length === 0 && (
                                        <tr>
                                            <td className="ps-3 text-body-tertiary" colSpan={9}>データがありません</td>
                                        </tr>
                                    )}
                                    {!usagesError && !usagesLoading && usages.map((r, i) => (
                                        <tr key={i}>
                                            <td className="ps-3">{r.spotName || ''}</td>
                                            <td className="ps-3">{r.packageName || ''}</td>
                                            <td className="ps-3">{fmtDateTime(r.createdAt)}</td>
                                            <td className="ps-3">{fmtDateTime(r.usedDate)}</td>
                                            <td className="ps-3">{fmtDateTime(r.effectivePeriodEndDate)}</td>
                                            <td className="ps-3">{statusLabel(r.tokenStatus)}</td>
                                            <td className="ps-3">{fmtInt(r.countAdult)}</td>
                                            <td className="ps-3">{fmtInt(r.countChild)}</td>
                                            <td className="ps-3">{r.voucherUseId || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {uTotal === 0 ? '0 items' : `${uFirst} to ${uLast} items of ${uTotal}`}
                                </p>
                            </div>
                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={uPrev} disabled={uPage <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>
                                <ul className="mb-0 pagination">
                                    {uPages.map(p => (
                                        <li key={p} className={`page-item ${p === uPage ? 'active' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setUPage(p)}>
                                                {p}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                <button type="button" className="page-link pe-0" onClick={uNext} disabled={uPage >= uPageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default UserDetail;
